//----------------Angular Imports-----------------//
import { isPlatformBrowser } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

//---------------Third-Party Imports---------------//
import { Card, DonutChartConfig, DonutGraphComponent, Skeleton } from '@ntv360/component-pantry';
import { catchError, EMPTY, forkJoin, map, Observable, of, tap } from 'rxjs';

//------------------Local Imports------------------//
import { ApiGetAllResponse, ApiUser } from '@core';
import { AuthService } from '@core/services/auth/auth.service';
import { ApiDashboardRaspi } from '@core/services/dashboard/dashboard.interfaces';
import { DashboardService } from '@core/services/dashboard/dashboard.service';
import { ApiDealerInformation } from '@core/services/dealer/dealer.interface';
import { DealerService } from '@core/services/dealer/dealer.service';
import { HostsService } from '@core/services/hosts/hosts.service';
import { LayoutService } from '@core/services/layout/layout.service';
import {
    ApiLicense,
    LicenseStats,
    PlayerMetric,
} from '@core/services/licenses/licenses.interfaces';
import { LicensesService } from '@core/services/licenses/licenses.service';
import { MediaLibraryService as AssetService } from '@core/services/media-library/media-library.service';
import { UsersService } from '@core/services/users/users.service';
import {
    DashboardActivityItem,
    DashboardDatasetKey,
    DashboardStatCard,
    DashboardUserTraits,
} from '@features/dashboard/interfaces/dashboard.interfaces';
import { StatsCardComponent } from '@shared';
import { EmptyComponent } from '@shared/components';
import { SVG_ICONS } from '@shared/constants';
import { StatsData } from '@shared/interfaces';
import {
    formatDateLong,
    formatDateTime,
    getCurrentWeekRange,
    isWithinCurrentWeek,
    Logger,
    withLoading,
} from '@shared/utils';
import {
    CHART_TYPE_OPTIONS,
    GRAPH_LEGEND_ITEMS,
    MONTH_OPTIONS,
    STAT_CARDS_CONFIG,
    StatCardConfig,
} from './constants/dashboard.constants';

type RecentActivityType = 'dealer' | 'user' | 'asset' | 'license';

interface RecentActivityListItem {
    createdAt: string;
    createdBy: string;
    label: string;
    action: string;
    type: RecentActivityType;
}

/**
 * High-level dashboard component providing a centralized overview of the N-Compass ecosystem.
 *
 * Orchestrates data fetching for hosts, dealers, advertisers, and licenses across various
 * time scales, providing real-time aggregations, growth charts, and role distribution analysis.
 * Leverages Angular Signals for granular reactivity and optimized rendering performance.
 *
 * @class DashboardComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'app-dashboard',
    imports: [Card, Skeleton, DonutGraphComponent, StatsCardComponent, EmptyComponent],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
    standalone: true,
})
export class DashboardComponent implements OnInit, OnDestroy {
    //-----------------Private Services-----------------//
    /** Service for reading the authenticated user traits. */
    private readonly authService = inject(AuthService);

    /** Service for managing dashboard statistics and data via global endpoint. */
    private readonly dashboardService = inject(DashboardService);

    /** Service for managing host location data, providing methods to fetch and cache site information. */
    private readonly hostsService = inject(HostsService);

    /** Service for managing dealer and advertiser accounts, handling hierarchies and client assignments. */
    private readonly dealerService = inject(DealerService);

    /** Service for managing licenses, device hardware metadata, and installation status. */
    private readonly licensesService = inject(LicensesService);

    /** Service for managing media assets/content used by the dashboard overview. */
    private readonly assetService = inject(AssetService);

    /** Service for managing system users, roles, and administrative permissions. */
    private readonly usersService = inject(UsersService);

    /** Service for coordinating shell-level layout behavior. */
    private readonly layoutService = inject(LayoutService);

    /** Logged-in user traits from the auth session storage. */
    private readonly currentUserTraits = signal<DashboardUserTraits | null>(
        this.authService.getUserTraits<DashboardUserTraits>(),
    );

    /** True when the current session is an admin or super admin account. */
    protected readonly isAdminAccount = computed(() =>
        this.isAdminTraits(this.currentUserTraits()),
    );

    //-----------------Private/Protected Signals and Declarations-----------------//
    /**
     * Managed reference for automatic RxJS subscription cleanup.
     * Utilized by `takeUntilDestroyed` to prevent memory leaks in the dashboard.
     */
    private readonly destroyRef = inject(DestroyRef);

    /** Identifier for the current execution platform (e.g., 'browser' or 'server'). */
    private readonly platformId = inject(PLATFORM_ID);

    /** Boolean flag indicating if the component is currently executing in a browser environment. */
    protected readonly isBrowser = isPlatformBrowser(this.platformId);

    /** Responsive breakpoint that matches the dashboard SCSS breakpoint. */
    private readonly responsiveBreakpoint = '(max-width: 1520px), (max-height: 860px)';

    /** Cached media query used to sync shell overflow with responsive state. */
    private responsiveMediaQuery: MediaQueryList | null = null;

    /**
     * Reactive signal holding the raw total counts for each primary dataset.
     * Updated via `loadOverview` and used by `statsData` to populate card totals.
     */
    private readonly overviewTotals = signal<Record<DashboardDatasetKey, number> | null>(null);

    /**
     * Reactive signal holding server-computed week stats for each primary dataset.
     */
    private readonly overviewStats = signal<Record<
        DashboardDatasetKey,
        { thisWeek: number; lastWeek: number }
    > | null>(null);

    /**
     * Reactive signal holding content stats (images and videos) from the global endpoint.
     * Used for the player-stats cards at the bottom of the dashboard.
     */
    private readonly contentStats = signal<{
        totalImages: { thisWeek: number; lastWeek: number; twoWeeksAgo: number; overall: number };
        totalVideos: { thisWeek: number; lastWeek: number; twoWeeksAgo: number; overall: number };
    } | null>(null);

    /**
     * Reactive signal holding player UI and Server version info from the global endpoint.
     * Used for Player UI and Player Server cards in the player-stats section.
     */
    private readonly playerInfo = signal<{
        playerUi: { currentVersion: string; dateCreated: string };
        playerServer: { currentVersion: string; dateCreated: string };
    } | null>(null);

    /** Raspberry Pi version buckets for the donut chart (`GET /api/dashboard/raspi`). */
    private readonly piVersions = signal<Array<{ label: string; total: number }>>([]);

    /** Uploaded content items shown in the second bottom card. */
    public readonly uploadedContentItems = signal<Array<{ title: string; createdAtLabel: string }>>(
        [],
    );

    /**
     * Loading state for the top-level overview cards and the recent activity feed.
     * True when `getOverview()` is in flight.
     */
    protected readonly isOverviewLoading = signal<boolean>(false);

    /**
     * Loading state for the primary growth chart.
     * True when `getChartData()` is in flight.
     */
    protected readonly isChartLoading = signal<boolean>(false);

    /** Whether there are more activity items to load. */
    public readonly hasMoreActivity = signal<boolean>(false);

    /** Loading state specifically for the "load more" action — only affects the bottom of the list. */
    public readonly isLoadingMoreActivity = signal<boolean>(false);

    /** Current page for activity pagination. */
    private readonly activityPage = signal<number>(1);

    /** Reference to the recent-activity `<ul>` used as IntersectionObserver root. */
    private readonly activityListRef = viewChild<ElementRef<HTMLElement>>('activityListRef');

    /** Sentinel element at the end of the activity list — triggers infinite scroll when visible. */
    private readonly activitySentinel = viewChild<ElementRef<HTMLElement>>('activitySentinel');

    /** IntersectionObserver instance driving infinite scroll on the activity list. */
    private intersectionObserver: IntersectionObserver | null = null;

    /**
     * Loading state for the primary growth chart.
     * True when `getChartData()` is in flight.
     */
    protected readonly isUploadedContentsLoading = signal<boolean>(false);

    /**
     * Loading state for the primary growth chart.
     * True when `getChartData()` is in flight.
     */
    protected readonly isActivityLoading = signal<boolean>(false);

    /**
     * Loading state for the User Distribution card.
     */
    protected readonly isUsersDistLoading = signal<boolean>(false);

    /**
     * Loading state for the Licenses-based cards (Pi Versions, Top Dealers).
     */
    protected readonly isLicensesLoading = signal<boolean>(false);

    /**
     * Loading state for the secondary summary cards at the bottom of the page.
     * True when `getPiVersions`, `getTopDealers`, etc., are being fetched.
     */
    protected readonly isExtraCardsLoading = signal<boolean>(false);

    //-----------------Public Signals and Declarations-----------------//
    /**
     * Boolean signal reflecting the global loading status of license-related operations.
     * Used by the UI to disable interactions or show loading spinners during background syncs.
     */
    public readonly isLoading = signal<boolean>(true);

    /** The current system date, refreshed on component init and used as a base for relative time calculations. */
    public readonly today = new Date();

    /** Subtitle text for the recent activity card, showing the current week range. */
    public readonly recentActivityWeekRangeLabel = computed((): string => {
        const { start, end } = getCurrentWeekRange(this.today);

        return `From ${formatDateLong(start)} to ${formatDateLong(end)}`;
    });

    /**
     * Signal tracking the currently active dataset for the growth chart and header statistics.
     * Defaults to 'hosts'. Impacted by clicks on the upper statistics cards.
     */
    public readonly selectedDataset = signal<DashboardDatasetKey>(
        this.isAdminAccount() ? 'dealers' : 'licenses',
    );

    /** signal holding the latest activities retrieved from the system feed, rendered in the right sidebar. */
    public readonly recentActivity = signal<DashboardActivityItem[]>([]);

    /** Signal holding the latest self-authored items from dealers, users, and assets. */
    public readonly recentActivityList = signal<RecentActivityListItem[]>([]);

    /** Reference to the shared SVG icon identifier constants used across the dashboard template. */
    public readonly icons = SVG_ICONS;

    /** Static collection of legend indicators mapping chart series colors to their semantic meanings. */
    public readonly legendItems = GRAPH_LEGEND_ITEMS;

    /** The absolute path string for the main SVG icon sprite asset. */
    public readonly spritePath = 'assets/icons/icons-sprite.svg';

    /** Standard collection of month names and indices for use in the Month dropdown. */
    public readonly MONTH_OPTIONS = MONTH_OPTIONS;

    /** Collection of chart presentation options for the Switch Graph dropdown. */
    public readonly CHART_TYPE_OPTIONS = CHART_TYPE_OPTIONS;

    /** Expose the shared date formatter to the template. */
    public readonly formatDateTime = formatDateTime;

    /** Labels for the recent-activity source type. */
    public readonly recentActivityTypeLabels: Record<RecentActivityType, string> = {
        asset: 'Asset',
        dealer: 'Dealer',
        user: 'User',
        license: 'License',
    };

    //-----------------Computed Signals-----------------//
    /**
     * Derives a structured array of statistics card data from server-side totals and
     * pre-computed weekly stats.
     *
     * @returns {StatsData[]} Compiled list of stat card objects for the template.
     */
    public readonly statsData = computed<DashboardStatCard[]>(() => {
        const totals = this.overviewTotals();
        const statsMap = this.overviewStats();

        const selected = this.selectedDataset();

        return STAT_CARDS_CONFIG.map((config: StatCardConfig) => {
            const total = totals ? totals[config.datasetKey] || 0 : 0;
            const weeklyStats = statsMap?.[config.datasetKey] ?? { thisWeek: 0, lastWeek: 0 };

            return this.createStatCard(
                config.datasetKey,
                config.title,
                total,
                weeklyStats.thisWeek,
                weeklyStats.lastWeek,
                config.iconKey,
                selected === config.datasetKey ? 'dark' : 'light',
                config.customColor,
            );
        });
    });

    public readonly playerStatCards = computed<StatsData[]>(() => {
        const playerUi = this.playerInfo()?.playerUi;
        const playerServer = this.playerInfo()?.playerServer;

        // Helper to extract time from date string
        const extractTime = (dateStr: string | undefined): string => {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        };

        // Helper to format date
        const formatDate = (dateStr: string | undefined): string => {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        };

        return [
            {
                title: 'Player UI',
                description: '',
                iconKey: 'installations',
                total: `Version: ${playerUi?.currentVersion ?? '—'}`,
                stats: [
                    {
                        label: 'Created on:',
                        value: formatDate(playerUi?.dateCreated),
                        colorClass: '',
                    },
                    {
                        label: 'Posted on:',
                        value: extractTime(playerUi?.dateCreated),
                        colorClass: '',
                    },
                ],
            },
            {
                title: 'Player Server',
                description: '',
                iconKey: 'host',
                total: `Version: ${playerServer?.currentVersion ?? '—'}`,
                stats: [
                    {
                        label: 'Created on:',
                        value: formatDate(playerServer?.dateCreated),
                        colorClass: '',
                    },
                    {
                        label: 'Posted on:',
                        value: extractTime(playerServer?.dateCreated),
                        colorClass: '',
                    },
                ],
            },
        ];
    });

    public readonly contentStatCards = computed<StatsData[]>(() => {
        const videos = this.contentStats()?.totalVideos;
        const images = this.contentStats()?.totalImages;

        return [
            {
                title: 'Videos',
                description: '',
                iconKey: 'videos',
                customColor: { iconColor: '#E73535', bgColor: '#FDF1F1' },
                total: (videos?.overall ?? 0).toString(),
                stats: [
                    {
                        label: 'This Week',
                        value: (videos?.thisWeek ?? 0).toString(),
                        colorClass: '',
                    },
                    {
                        label: 'Last Week',
                        value: (videos?.lastWeek ?? 0).toString(),
                        colorClass: '',
                    },
                ],
            },
            {
                title: 'Images',
                description: '',
                iconKey: 'images',
                customColor: { iconColor: '#095AF3', bgColor: '#EEF4FE' },
                total: (images?.overall ?? 0).toString(),
                stats: [
                    {
                        label: 'This Week',
                        value: (images?.thisWeek ?? 0).toString(),
                        colorClass: '',
                    },
                    {
                        label: 'Last Week',
                        value: (images?.lastWeek ?? 0).toString(),
                        colorClass: '',
                    },
                ],
            },
        ];
    });

    /**
     * Visible cards on the dashboard, based on the current account type.
     */
    public readonly visibleStatsData = computed(() => {
        if (this.isAdminAccount()) {
            return this.statsData();
        }

        return this.statsData().filter(
            (card) => card.datasetKey === 'licenses' || card.datasetKey === 'assets',
        );
    });

    /**
     * Configuration object for the donut chart.
     * Re-calculates total count based on processed `piVersionsData`.
     *
     * @returns {DonutChartConfig} The visual and data configuration for the donut component.
     */
    public readonly donutConfig = computed<DonutChartConfig>(() => ({
        iconName: 'RASPBERRY',
        title: 'Raspberry Pi Versions',
        size: 'small',
        showLegend: true,
        legendPosition: 'right',
        totalCount: this.piVersionsData().reduce((acc, curr) => acc + curr.total, 0),
        colors: ['#1e88f5', '#c8b4f5', '#4ade80', '#fb7185', '#fbbf24', '#84cc16', '#a855f7'],
    }));

    public readonly piVersionsData = computed(() => this.piVersions());

    /**
     * Computed greeting message combining client type and first name.
     * Displays as "Welcome {clientTypeName} {firstName}!" or falls back to "Welcome!"
     *
     * @returns {string} The dynamic greeting text.
     */
    public readonly welcomeGreeting = computed<string>(() => {
        const traits = this.currentUserTraits();
        if (!traits?.data) return 'Welcome!';

        const clientType = traits.data.clientTypeName || 'Admin';
        const firstName = traits.data.firstName || 'User';
        return `Welcome ${clientType} ${firstName}!`;
    });

    /**
     * Display name for the current user, used as the uploader/actor label
     * on each Recent Activity row (rows are filtered to the current user).
     */
    public readonly currentUserName = computed<string>(() => {
        const data = this.currentUserTraits()?.data;
        if (!data) return '';
        return (
            [data.firstName, data.lastName].filter(Boolean).join(' ') ||
            data.userName ||
            data.emailAddress ||
            ''
        );
    });

    constructor() {
        effect(() => {
            const sentinel = this.activitySentinel();
            const listRef = this.activityListRef();

            this.intersectionObserver?.disconnect();
            this.intersectionObserver = null;

            if (!this.isBrowser || !sentinel || !listRef) return;

            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (
                        entry.isIntersecting &&
                        this.hasMoreActivity() &&
                        !this.isLoadingMoreActivity()
                    ) {
                        this.loadRecentActivityList(this.activityPage() + 1);
                    }
                },
                { root: listRef.nativeElement, rootMargin: '100px' },
            );

            this.intersectionObserver.observe(sentinel.nativeElement);
        });
    }

    /**
     * Angular lifecycle hook. Fetches initial data and oldest-year range.
     *
     * @returns {void}
     */
    public ngOnInit(): void {
        this.currentUserTraits.set(this.authService.getUserTraits<DashboardUserTraits>());
        this.syncShellOverflowWithLayout();
        this.refreshData();
    }

    /**
     * Restores shell scrolling when the dashboard is destroyed.
     *
     * @returns {void}
     */
    public ngOnDestroy(): void {
        this.layoutService.setOverflowHidden(false);
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;

        if (this.responsiveMediaQuery !== null) {
            this.responsiveMediaQuery.removeEventListener('change', this.handleResponsiveChange);
        }
    }

    /** Keeps the shell overflow state aligned with the dashboard's responsive breakpoint. */
    private syncShellOverflowWithLayout(): void {
        if (!this.isBrowser) {
            return;
        }

        this.responsiveMediaQuery = window.matchMedia(this.responsiveBreakpoint);
        this.layoutService.setOverflowHidden(!this.responsiveMediaQuery.matches);
        this.responsiveMediaQuery.addEventListener('change', this.handleResponsiveChange);
    }

    /** Updates shell overflow whenever the responsive breakpoint changes. */
    private handleResponsiveChange = (event: MediaQueryListEvent): void => {
        this.layoutService.setOverflowHidden(!event.matches);
    };

    /**
     * Triggers a global refresh of cached data by clearing internal service caches.
     * Reloads all dashboard metrics via the reactive stream to ensure 100% data freshness.
     *
     * @returns {void}
     */
    public refreshData(): void {
        this.licensesService.clearCache();
        this.hostsService.clearCache();
        this.dealerService.clearCache();
        this.usersService.clearCache();

        this.overviewTotals.set(null);
        this.overviewStats.set(null);
        this.contentStats.set(null);
        this.playerInfo.set(null);
        this.piVersions.set([]);
        this.recentActivityList.set([]);
        this.uploadedContentItems.set([]);
        this.hasMoreActivity.set(false);
        this.activityPage.set(1);

        this.loadOverview();
        this.loadExtraCards();
    }

    /**
     * Loads the overview statistics for the dashboard using the global endpoint.
     * Also calls licensesService.getStats() to get player UI and Server version info.
     *
     * @private
     * @returns {void}
     */
    private loadOverview(): void {
        const isAdmin = this.isAdminAccount();
        this.isOverviewLoading.set(true); // show skeleton immediately

        // Combine global dashboard data with licenses stats (for player versions)
        forkJoin({
            global: this.dashboardService.getGlobal(),
            licenseStats: this.licensesService.getStats(),
        })
            .pipe(
                tap(({ global, licenseStats }) => {
                    const globalData = global as unknown as {
                        data: {
                            dealers: {
                                totalDealers: {
                                    thisWeek: number;
                                    lastWeek: number;
                                    overall: number;
                                };
                            };
                            licenses: LicenseStats;
                            users: { all: { thisWeek: number; lastWeek: number; overall: number } };
                            player: {
                                playerServer: PlayerMetric;
                                playerUi: PlayerMetric;
                            };
                            contents: {
                                totalContents: {
                                    thisWeek: number;
                                    lastWeek: number;
                                    overall: number;
                                };
                                totalImages: {
                                    thisWeek: number;
                                    lastWeek: number;
                                    twoWeeksAgo: number;
                                    overall: number;
                                };
                                totalVideos: {
                                    thisWeek: number;
                                    lastWeek: number;
                                    twoWeeksAgo: number;
                                    overall: number;
                                };
                            };
                        };
                    };

                    const dlr = globalData.data.dealers;
                    const lic = globalData.data.licenses;
                    const usr = globalData.data.users;
                    const ast = globalData.data.contents;
                    const plr = globalData.data.player;

                    if (!isAdmin) {
                        // Non-admin users only see licenses and assets
                        this.overviewTotals.set({
                            dealers: 0,
                            advertisers: 0,
                            licenses:
                                lic.totalAds.overall +
                                lic.totalMenu.overall +
                                lic.totalClosed.overall +
                                lic.totalUnassigned.overall,
                            users: 0,
                            assets: ast.totalContents.overall,
                        });
                        this.overviewStats.set({
                            dealers: { thisWeek: 0, lastWeek: 0 },
                            advertisers: { thisWeek: 0, lastWeek: 0 },
                            licenses: {
                                thisWeek:
                                    lic.totalAds.thisWeek +
                                    lic.totalMenu.thisWeek +
                                    lic.totalClosed.thisWeek +
                                    lic.totalUnassigned.thisWeek,
                                lastWeek:
                                    lic.totalAds.lastWeek +
                                    lic.totalMenu.lastWeek +
                                    lic.totalClosed.lastWeek +
                                    lic.totalUnassigned.lastWeek,
                            },
                            users: { thisWeek: 0, lastWeek: 0 },
                            assets: {
                                thisWeek: ast.totalContents.thisWeek,
                                lastWeek: ast.totalContents.lastWeek,
                            },
                        });
                    } else {
                        // Admin users see all dashboard statistics
                        const licenseTotals =
                            lic.totalAds.overall +
                            lic.totalMenu.overall +
                            lic.totalClosed.overall +
                            lic.totalUnassigned.overall;
                        const licenseThisWeek =
                            lic.totalAds.thisWeek +
                            lic.totalMenu.thisWeek +
                            lic.totalClosed.thisWeek +
                            lic.totalUnassigned.thisWeek;
                        const licenseLastWeek =
                            lic.totalAds.lastWeek +
                            lic.totalMenu.lastWeek +
                            lic.totalClosed.lastWeek +
                            lic.totalUnassigned.lastWeek;

                        this.overviewTotals.set({
                            dealers: dlr.totalDealers.overall,
                            advertisers: 0,
                            licenses: licenseTotals,
                            users: usr.all.overall,
                            assets: ast.totalContents.overall,
                        });
                        this.overviewStats.set({
                            dealers: {
                                thisWeek: dlr.totalDealers.thisWeek,
                                lastWeek: dlr.totalDealers.lastWeek,
                            },
                            advertisers: { thisWeek: 0, lastWeek: 0 },
                            licenses: { thisWeek: licenseThisWeek, lastWeek: licenseLastWeek },
                            users: { thisWeek: usr.all.thisWeek, lastWeek: usr.all.lastWeek },
                            assets: {
                                thisWeek: ast.totalContents.thisWeek,
                                lastWeek: ast.totalContents.lastWeek,
                            },
                        });
                    }

                    // Set content stats for player-stats cards
                    this.contentStats.set({
                        totalImages: globalData.data.contents.totalImages,
                        totalVideos: globalData.data.contents.totalVideos,
                    });

                    this.playerInfo.set({
                        playerUi: plr.playerUi,
                        playerServer: plr.playerServer,
                    });
                }),
                withLoading(this.isOverviewLoading),
                catchError((error: unknown) => {
                    Logger.error('DashboardComponent', 'Error loading overview data:', error);
                    this.isOverviewLoading.set(false);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /**
     * Parallelizes the retrieval of secondary data cards.
     * Computes role-based user distribution and initial license metrics concurrently
     * to reduce perceived dashboard latency.
     *
     * @private
     * @returns {void}
     */
    private loadExtraCards(): void {
        this.loadUploadedContents();
        this.loadRecentActivityList();

        this.loadRaspiVersions();
    }

    /**
     * Loads the latest uploaded content titles from the media library API.
     * Only the title field is needed for the dashboard list.
     */
    private loadUploadedContents(): void {
        const query = {
            page: 1,
            limit: 10,
            sortBy: 'createdAt',
            sortOrder: 'desc' as const,
            fields: ['title', 'createdAt'],
        };

        this.assetService
            .getContents(query)
            .pipe(
                withLoading(this.isUploadedContentsLoading),
                map((response) =>
                    response.data.map((item) => ({
                        title: item.title,
                        createdAtLabel: item.createdAt ? formatDateTime(item.createdAt) : '',
                    })),
                ),
                catchError((error: unknown) => {
                    Logger.error('DashboardComponent', 'Error loading uploaded contents:', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (items) => this.uploadedContentItems.set(items),
            });
    }

    /**
     * Loads recent self-authored records across dealers, users, and assets.
     * Only items created by the currently logged-in user are retained.
     */
    private loadRecentActivityList(page = 1): void {
        const currentUserId = this.currentUserTraits()?.data.userId;

        if (!currentUserId) {
            this.recentActivityList.set([]);
            return;
        }

        const query = {
            page,
            limit: 40,
            sortBy: 'createdAt',
            sortOrder: 'desc' as const,
            fields: ['createdAt', 'createdBy'],
        };

        if (page === 1) {
            this.isActivityLoading.set(true);
        } else {
            this.isLoadingMoreActivity.set(true);
        }

        const isAdmin = this.isAdminAccount();

        const licenseQuery = {
            ...query,
            fields: ['createdAt', 'createdBy', 'alias', 'licenseKey'],
        };

        forkJoin({
            dealers: isAdmin
                ? this.dealerService.getAll(query)
                : of(this.createEmptyPage<ApiDealerInformation>()),
            users: isAdmin ? this.usersService.getAll(query) : of(this.createEmptyPage<ApiUser>()),
            assets: this.assetService.getContents(query),
            licenses: this.licensesService
                .getAll(licenseQuery)
                .pipe(catchError(() => of(this.createEmptyPage<ApiLicense>()))),
        })
            .pipe(
                map(({ dealers, users, assets, licenses }) => {
                    const allRaw = [
                        ...dealers.data.map((item) => ({
                            createdAt: item.clientInformation.createdAt,
                            createdBy: item.clientInformation.createdBy,
                            label: item.clientInformation.businessName,
                            action: 'Created',
                            type: 'dealer' as const,
                        })),
                        ...users.data.map((item) => ({
                            createdAt: item.createdAt,
                            createdBy: item.createdBy,
                            label:
                                [item.firstName, item.lastName].filter(Boolean).join(' ') ||
                                item.username ||
                                item.emailAddress,
                            action: 'Created',
                            type: 'user' as const,
                        })),
                        ...assets.data.map((item) => ({
                            createdAt: item.createdAt,
                            createdBy: item.createdBy,
                            label: item.title,
                            action: 'Uploaded',
                            type: 'asset' as const,
                        })),
                        ...licenses.data.map((item) => ({
                            createdAt: item.createdAt ?? '',
                            createdBy: item.createdBy ?? '',
                            label: item.alias || item.licenseKey || '',
                            action: 'Generated',
                            type: 'license' as const,
                        })),
                    ].filter((item): item is RecentActivityListItem => Boolean(item.createdAt));

                    const items = allRaw
                        .filter(
                            (item) =>
                                item.createdBy === currentUserId &&
                                isWithinCurrentWeek(item.createdAt),
                        )
                        .sort(
                            (a, b) =>
                                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                        );

                    const apiHasMore =
                        dealers.meta.pagination.hasNextPage ||
                        users.meta.pagination.hasNextPage ||
                        assets.meta.pagination.hasNextPage ||
                        licenses.meta.pagination.hasNextPage;

                    // Stop paginating once a page yields zero in-week records;
                    // sources are sorted createdAt desc so deeper pages are older.
                    const anyInWeek = allRaw.some((item) => isWithinCurrentWeek(item.createdAt));
                    const hasMore = apiHasMore && anyInWeek;

                    return { items, hasMore };
                }),
                catchError((error: unknown) => {
                    Logger.error(
                        'DashboardComponent',
                        'Error loading recent activity data:',
                        error,
                    );
                    this.isActivityLoading.set(false);
                    this.isLoadingMoreActivity.set(false);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: ({ items, hasMore }) => {
                    // Commit this page's matching items immediately
                    if (page === 1) {
                        this.recentActivityList.set(items);
                        this.isActivityLoading.set(false);
                    } else if (items.length > 0) {
                        this.recentActivityList.update((current) => [...current, ...items]);
                    }

                    this.activityPage.set(page);
                    this.hasMoreActivity.set(hasMore);

                    // Eagerly walk every page in the current-week window
                    if (hasMore) {
                        this.loadRecentActivityList(page + 1);
                    } else {
                        this.isLoadingMoreActivity.set(false);
                    }
                },
            });
    }

    /** Loads the next page of activity items and appends them to the list. */
    public onSeeMoreActivity(event: Event): void {
        event.preventDefault();
        if (this.isLoadingMoreActivity() || !this.hasMoreActivity()) return;
        this.loadRecentActivityList(this.activityPage() + 1);
    }

    /**
     * Loads Raspberry Pi version distribution for the donut chart.
     *
     * @private
     * @returns {void}
     */
    private loadRaspiVersions(): void {
        this.isLicensesLoading.set(true);

        this.dashboardService
            .getRaspi()
            .pipe(
                map((response) => this.mapRaspiResponse(response)),
                catchError((error: unknown) => {
                    Logger.error('DashboardComponent', 'Error loading Raspberry Pi data:', error);
                    return of([]);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (items) => {
                    this.piVersions.set(items);
                    this.isLicensesLoading.set(false);
                },
                error: () => this.isLicensesLoading.set(false),
            });
    }

    /**
     * @private
     * @description Maps `/api/dashboard/raspi` payload into donut chart segments
     * @param response - API response with `data[]` of `{ model, count }`
     * @returns Donut chart rows sorted by count descending
     */
    private mapRaspiResponse(response: ApiDashboardRaspi): Array<{ label: string; total: number }> {
        return (response.data ?? [])
            .map((item) => {
                let label = (item.model ?? 'Unknown').trim();
                if (!label || label.toLowerCase() === 'unknown') {
                    label = 'Unknown Model';
                }
                const total = Number(item.count);
                return { label, total: Number.isFinite(total) ? total : 0 };
            })
            .filter((item) => item.total > 0)
            .sort((a, b) => b.total - a.total);
    }

    /**
     * Factory utility to construct a standardized `StatsData` object for the dashboard template.
     * Simplifies the mapping from raw API counts to UI-ready card models.
     *
     * @private
     * @param {string} title - Descriptive label for the statistic card.
     * @param {number} total - Overall cumulative count across all time periods.
     * @param {number} thisWeek - Total records created in the current calendar week.
     * @param {number} lastWeek - Total records created in the previous calendar week.
     * @param {string} iconKey - Semantic key for the SVG icon to display.
     * @param {'dark' | 'light'} [variant='light'] - Visual theme determining text/bg contrast.
     * @param {Object} [customColor] - Optional manual color overrides for the card icon.
     * @returns {StatsData} Structured object optimized for the `ntv-stat-card` interface.
     */
    private createStatCard(
        datasetKey: DashboardDatasetKey,
        title: string,
        total: number,
        thisWeek: number,
        lastWeek: number,
        iconKey: string,
        variant: 'dark' | 'light' = 'light',
        customColor?: { iconColor: string; bgColor: string },
    ): DashboardStatCard {
        return {
            datasetKey,
            title,
            description: '',
            iconKey,
            variant,
            customColor,
            total: `${total}`,
            stats: [
                { label: 'This Week', value: `${thisWeek}`, colorClass: 'text-accent-main' },
                { label: 'Last Week', value: `${lastWeek}`, colorClass: 'text-accent-main' },
            ],
        };
    }

    /**
     * Builds an empty paginated response for non-admin roles that should skip
     * dealer/user fetches in the recent-activity stream.
     */
    private createEmptyPage<T>(): ApiGetAllResponse<T> {
        return {
            data: [],
            meta: {
                timestamp: new Date().toISOString(),
                statusCode: 200,
                pagination: {
                    totalCount: 0,
                    page: 1,
                    limit: 10,
                    totalPages: 0,
                    hasNextPage: false,
                    hasPreviousPage: false,
                },
            },
        };
    }

    /**
     * Normalizes the auth role into a binary admin/non-admin flag.
     */
    private isAdminTraits(traits: DashboardUserTraits | null): boolean {
        const roleCode = traits?.data?.roleCode?.toLowerCase() ?? '';
        const roleName = traits?.data?.roleName?.toLowerCase() ?? '';
        const clientTypeName = traits?.data?.clientTypeName?.toLowerCase() ?? '';

        return [roleCode, roleName, clientTypeName].some(
            (value) => value === 'admin' || value === 'super_admin' || value === 'super admin',
        );
    }
}
